import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Button, CatalogIcon, SkubotSpinner } from "@commonsku/styles";
import { useDispatch, useSelector, useStore } from "react-redux";
import { SubPageHeader } from "../SubPageHeader";
import { DisplayClient, CatalogItemsSortField, nextCatalogItemsSortParam } from "../../../types";
import {
    fetchClientCatalog,
    createClientCatalog,
    fetchCatalogItems,
    fetchCatalogItemsBoth,
    resetState,
    selectCatalogId,
    selectLookedUpClientId,
    selectPublishedCatalogItems,
    selectUnpublishedCatalogItems,
    selectPublishedCatalogItemsCount,
    selectUnpublishedCatalogItemsCount,
    selectPublishedCatalogItemsHasMore,
    selectUnpublishedCatalogItemsHasMore,
    selectPublishedCatalogItemsCursor,
    selectUnpublishedCatalogItemsCursor,
    selectCatalogItemsLoadingMore,
    selectCatalogLoading,
    selectHasEverHadPublishedItems,
} from "../../../redux/catalog";
import { resetConvertibleProducts } from "../../../redux/convertibleProducts";
import { CatalogItemsPage } from "./CatalogItemsPage";
import { CatalogItemEditModal } from "./CatalogItemEditModal";
import { useCatalogItemEditContext } from "./CatalogItemEditContext";
import {
    CatalogExportActionsButtonDropdown,
    TAB_UNPUBLISHED,
    type CatalogItemsTab,
    EmptyStateWrapper,
    IconCircle,
    EmptyStateHeaderText,
    EmptyStateBodyText,
} from "./catalogShared";
import { reducers as coreReducers } from "../../../reducers/core";
import displayReducer from "../../../reducers/display";
import { createLoadDivision } from "../../../actions/supplier";
import { createLoadThemes } from "../../../actions/theme";
import { fetchAvalaraCategories } from "../../../actions/avalara";
import ProductSearchModal from "../../ProductSearchModal";

type Store = ReturnType<typeof useStore> & {
    attachReducers?: (newReducers: Record<string, unknown>, forceReplace?: boolean) => void;
};

interface CatalogPageProps {
    client: DisplayClient;
}

export interface CatalogItemsFilters {
    search: string;
    divisionId: string | null;
    status: string | null;
    is_upgradeable: string | null;
    sort: string | null;
}

const EMPTY_CATALOG_ITEMS_FILTERS: CatalogItemsFilters = {
    search: "",
    divisionId: null,
    status: null,
    is_upgradeable: null,
    sort: null,
};

function catalogItemsFiltersToFetchParams(filters: CatalogItemsFilters) {
    const search = filters.search.trim();
    return {
        search: search || undefined,
        divisionId: filters.divisionId || undefined,
        status: filters.status || undefined,
        is_upgradeable: filters.is_upgradeable || undefined,
        sort: filters.sort?.trim() || undefined,
    };
}

const CatalogContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 22px;
`;

const FullWidthButton = styled(Button)`
    &&& {
        width: 100%;
    }
`;

const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
`;

const HeaderButtons = styled.div`
    &&& {
        display: flex;
        flex-direction: row;
        margin-left: auto;
        gap: 16px;
        height: 40px;
    }
`;

const catalogSpinner = (
    <LoadingWrapper>
        <SkubotSpinner />
    </LoadingWrapper>
);

// Product-search bootstrap for the catalog page
const REQUIRED_DIVISION_TYPES = [
    "connected-plus",
    "personal",
    "dc",
    "esp",
    "sage",
    "promostandards",
    "bookmark",
    "tagged-product",
] as const;

const DEFAULT_DISPLAY_LOADING = {
    add_item: [],
    added_item: [],
    upload_file: false,
    send_po: [],
};

const hydratedDisplayReducer = (state: any, action: any) => {
    const hydrated = {
        ...state,
        loading: { ...DEFAULT_DISPLAY_LOADING, ...(state?.loading || {}) },
    };
    return displayReducer(hydrated, action);
};

const CATALOG_PAGE_REDUCERS = { ...coreReducers, display: hydratedDisplayReducer };

interface AddProductModalProps {
    catalogId: string;
    clientId: string;
    onClose: () => void;
}

const CatalogAddProductModal = ({ catalogId, clientId, onClose }: AddProductModalProps) => {
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    return ReactDOM.createPortal(
        <DndProvider backend={HTML5Backend}>
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                }}
            >
                <ProductSearchModal
                    catalogId={catalogId}
                    clientId={clientId}
                    onClose={onClose}
                    headerText="Add Product to Catalog"
                />
            </div>
        </DndProvider>,
        document.body,
    );
};

export const CatalogPage = ({ client }: CatalogPageProps) => {
    const store = useStore() as Store;
    const dispatch = useDispatch();

    // Attach product-search reducers after mount so we don't update the store during render.
    useLayoutEffect(() => {
        if (store.attachReducers) {
            store.attachReducers(CATALOG_PAGE_REDUCERS, true);
        }
    }, [store]);

    const catalogId = useSelector(selectCatalogId);
    const lookedUpClientId = useSelector(selectLookedUpClientId);
    const publishedItems = useSelector(selectPublishedCatalogItems);
    const unpublishedItems = useSelector(selectUnpublishedCatalogItems);
    const publishedCount = useSelector(selectPublishedCatalogItemsCount);
    const unpublishedCount = useSelector(selectUnpublishedCatalogItemsCount);
    const publishedHasMore = useSelector(selectPublishedCatalogItemsHasMore);
    const unpublishedHasMore = useSelector(selectUnpublishedCatalogItemsHasMore);
    const publishedCursor = useSelector(selectPublishedCatalogItemsCursor);
    const unpublishedCursor = useSelector(selectUnpublishedCatalogItemsCursor);
    const loadingMore = useSelector(selectCatalogItemsLoadingMore);
    const loading = useSelector(selectCatalogLoading);
    const hasEverHadPublishedItems = useSelector(selectHasEverHadPublishedItems);
    const [filters, setFilters] = useState<CatalogItemsFilters>(EMPTY_CATALOG_ITEMS_FILTERS);
    const [activeTab, setActiveTab] = useState<CatalogItemsTab>(TAB_UNPUBLISHED);
    const [showAddModal, setShowAddModal] = useState(false);
    const [creatingCatalog, setCreatingCatalog] = useState(false);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const { setCatalogItemEditing } = useCatalogItemEditContext();

    useEffect(() => {
        setCatalogItemEditing(!!editingItemId);
        return () => setCatalogItemEditing(false);
    }, [editingItemId, setCatalogItemEditing]);

    const fetchBothTabs = useCallback(
        (nextFilters: CatalogItemsFilters) => {
            if (!catalogId) {
                return;
            }
            dispatch(
                fetchCatalogItemsBoth({
                    catalogId,
                    ...catalogItemsFiltersToFetchParams(nextFilters),
                }) as any,
            );
        },
        [catalogId, dispatch],
    );

    const fetchActiveTab = useCallback(
        (nextFilters: CatalogItemsFilters, tab: CatalogItemsTab = activeTab) => {
            if (!catalogId) {
                return;
            }
            dispatch(
                fetchCatalogItems({
                    catalogId,
                    hidden: tab === TAB_UNPUBLISHED ? 1 : 0,
                    ...catalogItemsFiltersToFetchParams(nextFilters),
                }) as any,
            );
        },
        [activeTab, catalogId, dispatch],
    );

    const handleLoadMore = useCallback(() => {
        if (!catalogId || loadingMore) {
            return;
        }
        const isUnpublished = activeTab === TAB_UNPUBLISHED;
        const hasMore = isUnpublished ? unpublishedHasMore : publishedHasMore;
        const cursor = isUnpublished ? unpublishedCursor : publishedCursor;
        if (!hasMore || !cursor) {
            return;
        }
        dispatch(
            fetchCatalogItems({
                catalogId,
                hidden: isUnpublished ? 1 : 0,
                cursor,
                ...catalogItemsFiltersToFetchParams(filters),
            }) as any,
        );
    }, [
        catalogId,
        loadingMore,
        activeTab,
        unpublishedHasMore,
        publishedHasMore,
        unpublishedCursor,
        publishedCursor,
        filters,
        dispatch,
    ]);

    const patchFilters = useCallback((patch: Partial<CatalogItemsFilters>) => {
        setFilters((current) => ({ ...current, ...patch }));
    }, []);

    const applyAndFetch = useCallback(() => {
        const next: CatalogItemsFilters = {
            ...filters,
            search: filters.search.trim(),
        };
        setFilters(next);
        fetchActiveTab(next);
    }, [filters, fetchActiveTab]);

    const handleSortField = useCallback(
        (field: CatalogItemsSortField) => {
            const next: CatalogItemsFilters = {
                ...filters,
                sort: nextCatalogItemsSortParam(filters.sort, field),
            };
            setFilters(next);
            fetchActiveTab(next);
        },
        [filters, fetchActiveTab],
    );

    const handleTabChange = useCallback(
        (tab: CatalogItemsTab) => {
            setActiveTab(tab);
            fetchActiveTab(filters, tab);
        },
        [filters, fetchActiveTab],
    );

    const handleAddProduct = useCallback(async () => {
        if (catalogId) {
            setShowAddModal(true);
            return;
        }
        setCreatingCatalog(true);
        try {
            const action: any = await dispatch(createClientCatalog(client.id) as any);
            if (action?.payload?.catalogId) {
                setShowAddModal(true);
            }
        } finally {
            setCreatingCatalog(false);
        }
    }, [catalogId, client.id, dispatch]);

    const handleAddModalClose = useCallback(() => {
        setShowAddModal(false);
        applyAndFetch();
    }, [applyAndFetch]);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            applyAndFetch();
        }
    };

    useEffect(() => {
        setFilters(EMPTY_CATALOG_ITEMS_FILTERS);
        setActiveTab(TAB_UNPUBLISHED);
        setEditingItemId(null);
        dispatch(fetchClientCatalog(client.id) as any);
        dispatch(fetchAvalaraCategories() as any);
        REQUIRED_DIVISION_TYPES.forEach((t) => dispatch(createLoadDivision(t) as any));
        dispatch(createLoadThemes() as any);
        return () => {
            dispatch(resetState());
            dispatch(resetConvertibleProducts() as any);
        };
    }, [client.id, dispatch]);

    useEffect(() => {
        if (!catalogId) {
            return;
        }
        fetchBothTabs(EMPTY_CATALOG_ITEMS_FILTERS);
    }, [catalogId, fetchBothTabs]);

    const hasMore = activeTab === TAB_UNPUBLISHED ? unpublishedHasMore : publishedHasMore;

    const renderContent = () => {
        if (loading) {
            return catalogSpinner;
        }
        if (!catalogId) {
            // initial fetch incomplete
            if (lookedUpClientId === null) {
                return null;
            }
            return (
                <EmptyStateWrapper>
                    <IconCircle>
                        <CatalogIcon />
                    </IconCircle>
                    <EmptyStateHeaderText>Build Your Client Catalog</EmptyStateHeaderText>
                    <EmptyStateBodyText>
                        Add products your client orders regularly — with variants, SKUs, and
                        decoration already configured. Once published, reps can pull them
                        straight into projects without starting from scratch.
                    </EmptyStateBodyText>
                    <FullWidthButton onClick={handleAddProduct} disabled={creatingCatalog}>Add Your First Product</FullWidthButton>
                </EmptyStateWrapper>
            );
        }

        return (
            <>
                <CatalogItemsPage
                    activeTab={activeTab}
                    publishedCount={publishedCount}
                    unpublishedCount={unpublishedCount}
                    publishedItems={publishedItems}
                    unpublishedItems={unpublishedItems}
                    onTabChange={handleTabChange}
                    filters={filters}
                    onFiltersChange={patchFilters}
                    onApplyFilters={applyAndFetch}
                    onSortField={handleSortField}
                    onSearchKeyDown={handleSearchKeyDown}
                    catalogId={catalogId}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    onLoadMore={handleLoadMore}
                    onAddProduct={handleAddProduct}
                    onEditItem={setEditingItemId}
                    hasEverHadPublishedItems={hasEverHadPublishedItems}
                />
                {editingItemId && (
                    <CatalogItemEditModal
                        catalogId={catalogId}
                        itemId={editingItemId}
                        onClose={() => {
                            setEditingItemId(null);
                            fetchActiveTab(filters);
                        }}
                    />
                )}
            </>
        );
    };

    return (
        <CatalogContainer>
            <SubPageHeader title="Catalog" clientName={client.name} />
            <HeaderButtons>
                <CatalogExportActionsButtonDropdown catalogId={catalogId} />
            </HeaderButtons>
            {renderContent()}
            {showAddModal && catalogId && (
            <CatalogAddProductModal
                catalogId={catalogId}
                clientId={client.id}
                onClose={handleAddModalClose}
            />
        )}
        </CatalogContainer>
    );
};
